import { useState } from 'react';
import { useAuth, apiFetch } from '../App';

export default function Profile() {
  const { user, login } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currency: user?.currency || 'BWP',
    dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
    preferredBookmakers: user?.preferredBookmakers || [],
  });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [message, setMessage] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);

  const isMobile = window.innerWidth < 768;

  const BOOKMAKERS = [
    { key: 'betway_bw', label: 'Betway' },
    { key: 'sunbet_bw', label: 'Sunbet' },
    { key: 'pstbets_bw', label: 'PST Bets' },
    { key: 'bbets_bw', label: 'BBets' },
    { key: 'bet267_bw', label: 'Bet267' },
    { key: 'betxplosion_bw', label: 'BetXplosion' },
  ];

  const toggleBookmaker = (key) => {
    setForm(f => ({
      ...f,
      preferredBookmakers: f.preferredBookmakers.includes(key)
        ? f.preferredBookmakers.filter(b => b !== key)
        : [...f.preferredBookmakers, key],
    }));
  };

  const saveProfile = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const data = await apiFetch('/users/me', {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      login(localStorage.getItem('oe_token'), data.user || { ...user, ...form });
      setMessage({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    setPasswordMessage(null);
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      return setPasswordMessage({ type: 'error', text: 'Please fill in all password fields.' });
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      return setPasswordMessage({ type: 'error', text: 'New passwords do not match.' });
    }
    if (passwordForm.newPassword.length < 8) {
      return setPasswordMessage({ type: 'error', text: 'New password must be at least 8 characters.' });
    }

    setSavingPassword(true);
    try {
      await apiFetch('/users/me/password', {
        method: 'PUT',
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      setPasswordMessage({ type: 'success', text: 'Password changed successfully.' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordMessage({ type: 'error', text: err.message || 'Failed to change password.' });
    } finally {
      setSavingPassword(false);
    }
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 720 }}>

      {/* Profile header */}
      <div className="card" style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', background: 'var(--accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22, color: '#080d17',
          flexShrink: 0,
        }}>{initials}</div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18 }}>{user?.name}</div>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>{user?.email}</div>
          <span className={`pill pill-${user?.plan === 'pro' ? 'teal' : user?.plan === 'elite' ? 'amber' : 'blue'}`} style={{ fontSize: 10, marginTop: 8, display: 'inline-block' }}>
            {(user?.plan || 'free').toUpperCase()} PLAN
          </span>
        </div>
      </div>

      {/* Edit profile */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Account Details</div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label className="input-label" style={{ display: 'block', marginBottom: 6 }}>Full Name</label>
            <input className="input-field" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="input-label" style={{ display: 'block', marginBottom: 6 }}>Email</label>
            <input className="input-field" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={{ width: '100%' }} />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
  <label className="input-label" style={{ display: 'block', marginBottom: 6 }}>Date of Birth</label>
  <input
    className="input-field"
    type="date"
    value={form.dateOfBirth}
    onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))}
    max={new Date(Date.now() - 18 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
    style={{ width: '100%', maxWidth: 220 }}
  />
</div>

        <div style={{ marginBottom: 20 }}>
          <label className="input-label" style={{ display: 'block', marginBottom: 6 }}>Currency</label>
          <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} style={{ fontSize: 13, maxWidth: 200 }}>
            <option value="BWP">BWP — Botswana Pula</option>
            <option value="ZAR">ZAR — South African Rand</option>
            <option value="USD">USD — US Dollar</option>
          </select>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label className="input-label" style={{ display: 'block', marginBottom: 10 }}>Preferred Bookmakers</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {BOOKMAKERS.map(bm => (
              <button
                key={bm.key}
                onClick={() => toggleBookmaker(bm.key)}
                className={form.preferredBookmakers.includes(bm.key) ? 'btn btn-primary' : 'btn btn-outline'}
                style={{ fontSize: 12, padding: '6px 14px' }}
              >
                {bm.label}
              </button>
            ))}
          </div>
        </div>

        {message && (
          <div style={{
            padding: '10px 14px', borderRadius: 'var(--radius-sm)', marginBottom: 14, fontSize: 13,
            background: message.type === 'success' ? 'rgba(18,201,142,0.08)' : 'rgba(240,71,71,0.08)',
            border: `1px solid ${message.type === 'success' ? 'rgba(18,201,142,0.2)' : 'rgba(240,71,71,0.2)'}`,
            color: message.type === 'success' ? 'var(--success)' : 'var(--danger)',
          }}>
            {message.text}
          </div>
        )}

        <button className="btn btn-primary" onClick={saveProfile} disabled={saving} style={{ fontSize: 13, padding: '10px 24px' }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Change password */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, marginBottom: 20 }}>Change Password</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
          <div>
            <label className="input-label" style={{ display: 'block', marginBottom: 6 }}>Current Password</label>
            <input className="input-field" type="password" value={passwordForm.currentPassword} onChange={e => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))} style={{ width: '100%', maxWidth: 320 }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
            <div>
              <label className="input-label" style={{ display: 'block', marginBottom: 6 }}>New Password</label>
              <input className="input-field" type="password" value={passwordForm.newPassword} onChange={e => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))} style={{ width: '100%' }} />
            </div>
            <div>
              <label className="input-label" style={{ display: 'block', marginBottom: 6 }}>Confirm New Password</label>
              <input className="input-field" type="password" value={passwordForm.confirmPassword} onChange={e => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))} style={{ width: '100%' }} />
            </div>
          </div>
        </div>

        {passwordMessage && (
          <div style={{
            padding: '10px 14px', borderRadius: 'var(--radius-sm)', marginBottom: 14, fontSize: 13,
            background: passwordMessage.type === 'success' ? 'rgba(18,201,142,0.08)' : 'rgba(240,71,71,0.08)',
            border: `1px solid ${passwordMessage.type === 'success' ? 'rgba(18,201,142,0.2)' : 'rgba(240,71,71,0.2)'}`,
            color: passwordMessage.type === 'success' ? 'var(--success)' : 'var(--danger)',
          }}>
            {passwordMessage.text}
          </div>
        )}

        <button className="btn btn-outline" onClick={savePassword} disabled={savingPassword} style={{ fontSize: 13, padding: '10px 24px' }}>
          {savingPassword ? 'Updating...' : 'Update Password'}
        </button>
      </div>

      {/* Danger zone */}
      <div className="card" style={{ padding: 24, border: '1px solid rgba(240,71,71,0.2)' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, marginBottom: 8, color: 'var(--danger)' }}>Danger Zone</div>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>
          Closing your account will permanently delete your bet history, bankroll data, and preferences. This cannot be undone.
        </div>
        <a
          href="mailto:support@oddsedgebw.co.bw?subject=Please close my account"
          className="btn btn-outline"
          style={{ fontSize: 13, padding: '10px 24px', borderColor: 'rgba(240,71,71,0.3)', color: 'var(--danger)', textDecoration: 'none', display: 'inline-flex' }}
        >
          Request Account Closure
        </a>
      </div>
    </div>
  );
}